import axios from 'axios';
import apiConfig from 'constants/apiConfig';

export type SitrepCollabEnvelope = {
  topic: string;
  type: string;
  payload: Record<string, unknown>;
};

type Handler = (envelope: SitrepCollabEnvelope) => void;

const HEARTBEAT_PREFIX = ':';

function authHeaders(): Record<string, string> {
  const token = sessionStorage['accessToken'];
  const isTeams = sessionStorage['isUsingTeams'] === 'true';
  return {
    Authorization: `Bearer ${token || ''}`,
    'x-teamsapp': isTeams ? 'true' : 'false',
  };
}

/** Authenticated SSE downlink + POST uplink for draft sitrep collab topics. */
export class SitrepCollabTransport {
  readonly sitrepId: string;
  private connectionId: string | null = null;
  private abortController: AbortController | null = null;
  private handlers = new Set<Handler>();
  private subscribedTopics = new Set<string>();
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private destroyed = false;
  private readyResolvers: Array<(id: string) => void> = [];

  constructor(sitrepId: string) {
    this.sitrepId = sitrepId;
  }

  get id(): string | null {
    return this.connectionId;
  }

  onMessage(handler: Handler): () => void {
    this.handlers.add(handler);
    return () => this.handlers.delete(handler);
  }

  async whenReady(): Promise<string> {
    if (this.connectionId) return this.connectionId;
    return new Promise((resolve) => {
      this.readyResolvers.push(resolve);
    });
  }

  start(): void {
    if (this.destroyed) return;
    void this.connect();
  }

  destroy(): void {
    this.destroyed = true;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.abortController?.abort();
    this.connectionId = null;
    this.handlers.clear();
    this.subscribedTopics.clear();
    this.readyResolvers = [];
  }

  async subscribe(topics: string[]): Promise<void> {
    for (const topic of topics) this.subscribedTopics.add(topic);
    const connectionId = await this.whenReady();
    await axios.post(`${apiConfig.apiGateway.sitrepsV2}/collab/subscribe`, {
      connectionId,
      sitrepId: this.sitrepId,
      topics,
    });
  }

  async unsubscribe(topics: string[]): Promise<void> {
    for (const topic of topics) this.subscribedTopics.delete(topic);
    if (!this.connectionId) return;
    await axios.post(`${apiConfig.apiGateway.sitrepsV2}/collab/unsubscribe`, {
      connectionId: this.connectionId,
      sitrepId: this.sitrepId,
      topics,
    });
  }

  async publish(
    topic: string,
    type: string,
    payload: Record<string, unknown>
  ): Promise<void> {
    const connectionId = await this.whenReady();
    await axios.post(`${apiConfig.apiGateway.sitrepsV2}/collab/publish`, {
      connectionId,
      sitrepId: this.sitrepId,
      topic,
      type,
      payload,
    });
  }

  private async connect(): Promise<void> {
    if (this.destroyed) return;
    this.abortController = new AbortController();

    try {
      const response = await fetch(
        `${apiConfig.apiGateway.sitrepsV2}/collab/stream?sitrep_id=${encodeURIComponent(this.sitrepId)}`,
        {
          headers: authHeaders(),
          signal: this.abortController.signal,
        }
      );
      if (!response.ok || !response.body) {
        throw new Error(`HTTP ${response.status}`);
      }

      this.reconnectAttempts = 0;
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (!this.destroyed) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split('\n\n');
        buffer = events.pop() || '';

        for (const event of events) {
          if (!event.trim() || event.trim().startsWith(HEARTBEAT_PREFIX)) {
            continue;
          }
          for (const line of event.split('\n')) {
            if (!line.startsWith('data: ')) continue;
            try {
              const data = JSON.parse(line.slice(6).trim()) as {
                type?: string;
                topic?: string;
                payload?: Record<string, unknown>;
              };

              if (data.type === 'connected' && data.payload?.connectionId) {
                this.connectionId = String(data.payload.connectionId);
                const resolvers = this.readyResolvers;
                this.readyResolvers = [];
                resolvers.forEach((r) => r(this.connectionId!));
                // Re-apply section subscriptions after reconnect (presence is auto-joined).
                const topics = [...this.subscribedTopics];
                if (topics.length > 0) {
                  void axios
                    .post(
                      `${apiConfig.apiGateway.sitrepsV2}/collab/subscribe`,
                      {
                        connectionId: this.connectionId,
                        sitrepId: this.sitrepId,
                        topics,
                      }
                    )
                    .catch((err) => {
                      console.error(
                        '[SitrepCollab] resubscribe after reconnect failed',
                        err
                      );
                    });
                }
                continue;
              }

              if (data.topic && data.type) {
                const envelope: SitrepCollabEnvelope = {
                  topic: data.topic,
                  type: data.type,
                  payload: data.payload ?? {},
                };
                this.handlers.forEach((h) => h(envelope));
              }
            } catch (err) {
              console.error('[SitrepCollab] parse error', err);
            }
          }
        }
      }

      if (!this.destroyed) this.scheduleReconnect();
    } catch (err) {
      if ((err as Error).name !== 'AbortError' && !this.destroyed) {
        console.error('[SitrepCollab] stream error', err);
        this.scheduleReconnect();
      }
    }
  }

  private scheduleReconnect(): void {
    this.connectionId = null;
    if (this.destroyed || this.reconnectAttempts >= 5) return;
    this.reconnectAttempts += 1;
    const delay = this.reconnectAttempts * 2000;
    this.reconnectTimer = setTimeout(() => {
      void this.connect();
    }, delay);
  }
}

const transports = new Map<
  string,
  { transport: SitrepCollabTransport; refs: number }
>();

export function getSitrepCollabTransport(
  sitrepId: string
): SitrepCollabTransport {
  let entry = transports.get(sitrepId);
  if (!entry) {
    const transport = new SitrepCollabTransport(sitrepId);
    entry = { transport, refs: 0 };
    transports.set(sitrepId, entry);
    transport.start();
  }
  entry.refs += 1;
  return entry.transport;
}

export function releaseSitrepCollabTransport(sitrepId: string): void {
  const entry = transports.get(sitrepId);
  if (!entry) return;
  entry.refs -= 1;
  if (entry.refs > 0) return;
  entry.transport.destroy();
  transports.delete(sitrepId);
}
